import React, { useState, useEffect } from 'react';
import {
  FileText, FileCheck, Target, Trophy, Calendar, RefreshCw, ArrowRight, Plus,
  CheckCircle, XCircle, Clock, AlertTriangle, Phone, MapPin, User, Building, BarChart3
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { TargetStrip } from './TargetStrip';

const API_URL = window.location.origin;

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', minimumFractionDigits: 0,
}).format(amount || 0);

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  site_visited: 'bg-purple-100 text-purple-700',
  quoted: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
};
const statusColor = (s) => STATUS_COLORS[s] || 'bg-slate-100 text-slate-700';

const QUICK_ACTIONS = [
  { to: '/sales/enquiries', icon: Plus, bg: 'bg-blue-50 hover:bg-blue-100', fg: 'text-blue-600', label: 'New Enquiry', id: 'qa-new-enquiry' },
  { to: '/sales/lead-management/new', icon: Calendar, bg: 'bg-orange-50 hover:bg-orange-100', fg: 'text-orange-600', label: 'Schedule Follow-up', id: 'qa-new-followup' },
  { to: '/sales/customers', icon: Building, bg: 'bg-emerald-50 hover:bg-emerald-100', fg: 'text-emerald-600', label: 'Sales Customers', id: 'qa-sales-customers' },
  { to: '/sales/analysis', icon: BarChart3, bg: 'bg-purple-50 hover:bg-purple-100', fg: 'text-purple-600', label: 'Sales Analysis', id: 'qa-sales-analysis' },
];

const FUNNEL = [
  { key: 'enquiries', label: 'Enquiries', icon: FileText, bg: 'bg-blue-50', fg: 'text-blue-600' },
  { key: 'quoted', label: 'Quoted', icon: FileCheck, bg: 'bg-purple-50', fg: 'text-purple-600' },
  { key: 'won', label: 'Won', icon: CheckCircle, bg: 'bg-green-50', fg: 'text-green-600' },
  { key: 'lost', label: 'Lost', icon: XCircle, bg: 'bg-red-50', fg: 'text-red-600' },
];

const EnquiryList = ({ title, testId, items, empty, subtitle, badge }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      <Link to="/sales/enquiries" className="text-sm text-blue-600 hover:underline">View all</Link>
    </div>
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">{empty}</p>
      ) : items.slice(0, 5).map((enq) => (
        <div key={enq.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{enq.company_name}</p>
            <p className="text-xs text-slate-500">{subtitle(enq)}</p>
          </div>
          <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${statusColor(badge(enq))}`}>
            {(badge(enq) || '').replace(/_/g, ' ')}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const FollowupCard = ({ followup, onOpen }) => (
  <div
    onClick={onOpen}
    className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors border border-slate-100"
  >
    <div className="flex items-start justify-between mb-2">
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
        followup.priority === 'high' ? 'bg-red-100 text-red-700' :
        followup.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
      }`}>{followup.priority}</span>
      <span className="text-xs text-slate-500 flex items-center gap-1">
        <Clock className="w-3 h-3" />{followup.scheduled_time || 'No time set'}
      </span>
    </div>
    <h4 className="font-medium text-slate-900 text-sm mb-1 truncate">{followup.title}</h4>
    <p className="text-xs text-slate-600 mb-2 flex items-center gap-1">
      <User className="w-3 h-3" />{followup.customer_name || followup.lead_name || 'Unknown'}
    </p>
    <div className="flex items-center gap-2">
      <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-700">
        {followup.followup_type?.replace('_', ' ') || 'general'}
      </span>
      {followup.location && (
        <span className="text-xs text-slate-400 flex items-center gap-1 truncate">
          <MapPin className="w-3 h-3" />{followup.location}
        </span>
      )}
    </div>
  </div>
);

const SalesDashboard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const [recentEnquiries, setRecentEnquiries] = useState([]);
  const [todaysFollowups, setTodaysFollowups] = useState([]);
  const [followupStats, setFollowupStats] = useState({ overdue_count: 0, upcoming_week_count: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const [anaRes, enqRes, followupRes] = await Promise.all([
        fetch(`${API_URL}/api/sales-crm/analytics`, { headers }),
        fetch(`${API_URL}/api/sales/enquiries?limit=50`, { headers }),
        fetch(`${API_URL}/api/notifications/todays-followups`, { headers }),
      ]);
      if (anaRes.ok) setSummary((await anaRes.json()).summary || {});
      if (enqRes.ok) setRecentEnquiries((await enqRes.json()).enquiries || []);
      if (followupRes.ok) {
        const data = await followupRes.json();
        setTodaysFollowups(data.todays_followups || []);
        setFollowupStats({ overdue_count: data.overdue_count || 0, upcoming_week_count: data.upcoming_week_count || 0 });
      }
      await fetch(`${API_URL}/api/notifications/generate-followup-reminders`, { method: 'POST', headers });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openQuotes = recentEnquiries.filter(e => ['draft', 'sent'].includes(e.quote_status));

  const statCards = [
    { id: 'enquiries', title: 'Enquiries', value: summary.enquiries || 0, sub: `${summary.quote_rate || 0}% quoted`, icon: FileText, bg: 'bg-blue-50', fg: 'text-blue-600' },
    { id: 'quoted-value', title: 'Quoted Value', value: formatCurrency(summary.quoted_value), sub: `${summary.quoted || 0} quotes`, icon: FileCheck, bg: 'bg-purple-50', fg: 'text-purple-600' },
    { id: 'won-value', title: 'Won Value', value: formatCurrency(summary.won_value), sub: `${summary.won || 0} won`, icon: Trophy, bg: 'bg-green-50', fg: 'text-green-600' },
    { id: 'win-rate', title: 'Win Rate', value: `${summary.win_rate || 0}%`, sub: 'Won vs lost quotes', icon: Target, bg: 'bg-indigo-50', fg: 'text-indigo-600' },
    { id: 'followups', title: 'Follow-ups Done', value: `${summary.followups_done || 0}/${summary.followups || 0}`, sub: 'Sales team efforts', icon: Phone, bg: 'bg-orange-50', fg: 'text-orange-600' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[calc(100vw-3rem)] md:max-w-none" data-testid="sales-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Dashboard</h1>
          <p className="text-slate-500 mt-1">Enquiries, quotes and sales team efforts at a glance</p>
        </div>
        <button onClick={fetchDashboardData} data-testid="dashboard-refresh-btn"
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((card) => (
          <div key={card.id} data-testid={`stat-${card.id}`} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
            <div className={`p-2 rounded-lg w-fit ${card.bg}`}><card.icon className={`w-5 h-5 ${card.fg}`} /></div>
            <p className="text-sm text-slate-500 mt-4">{card.title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{card.value}</p>
            <p className="text-xs text-slate-400 mt-1">{card.sub}</p>
          </div>
        ))}
      </div>

      <TargetStrip />

      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="todays-followups-widget">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg"><Calendar className="w-5 h-5 text-blue-600" /></div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Today's Follow-ups</h3>
              <p className="text-sm text-slate-500">{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {followupStats.overdue_count > 0 && (
              <div className="flex items-center gap-2 px-3 py-1 bg-red-50 rounded-full">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-red-700">{followupStats.overdue_count} Overdue</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
              <Clock className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-medium text-blue-700">{followupStats.upcoming_week_count} This Week</span>
            </div>
            <Link to="/sales/lead-management" className="text-sm text-blue-600 hover:underline font-medium">View All</Link>
          </div>
        </div>
        {todaysFollowups.length === 0 ? (
          <div className="text-center py-8 bg-slate-50 rounded-lg">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No follow-ups scheduled for today</p>
            <Link to="/sales/lead-management/new"
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
              <Plus className="w-4 h-4" /> Schedule Follow-up
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {todaysFollowups.slice(0, 6).map((f) => (
              <FollowupCard key={f.id} followup={f} onOpen={() => navigate(`/sales/lead-management/followups/${f.id}`)} />
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            {QUICK_ACTIONS.map(a => (
              <Link key={a.id} to={a.to} data-testid={a.id} className={`flex items-center justify-between p-3 rounded-lg transition-colors ${a.bg}`}>
                <div className="flex items-center gap-3">
                  <a.icon className={`w-5 h-5 ${a.fg}`} />
                  <span className="text-sm font-medium text-slate-700">{a.label}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Enquiry → Quote → Win Funnel</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {FUNNEL.map(s => (
              <div key={s.key} className={`text-center p-4 rounded-lg ${s.bg}`} data-testid={`pipeline-${s.key}`}>
                <s.icon className={`w-6 h-6 mx-auto mb-2 ${s.fg}`} />
                <p className="text-2xl font-bold text-slate-900">{summary[s.key] || 0}</p>
                <p className="text-sm text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-4">
            Conversion (enquiry to win): <span className="font-semibold text-slate-700" data-testid="conversion-rate">{summary.conversion_rate || 0}%</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EnquiryList title="Recent Enquiries" testId="recent-enquiries" items={recentEnquiries} empty="No enquiries yet"
          subtitle={(e) => e.enquiry_no} badge={(e) => e.status} />
        <EnquiryList title="Open Quotes" testId="open-quotes" items={openQuotes} empty="No open quotes"
          subtitle={(e) => `${e.quote_no || e.enquiry_no} • ${formatCurrency(e.quote_value || e.value)}`} badge={(e) => e.quote_status} />
      </div>
    </div>
  );
};

export default SalesDashboard;
